import { useState, type FormEvent } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Dialog } from '@/components/Dialog/Dialog';
import { TextField } from '@/components/TextField/TextField';
import { isValidSlug, slugFromName } from '@/lib/tenants';

import styles from './TenantForm.module.css';

export interface TenantDraft {
  extAppApiId: string;
  name: string;
  slug: string | null;
}

export interface TenantFormProps {
  busy?: boolean;
  error?: string | null;
  onSubmit: (draft: TenantDraft) => void;
  onCancel: () => void;
}

const SLUG_RULE = 'Use lowercase letters and numbers, with single hyphens between words.';

/** Provision a tenant for an app-api organization. */
export function TenantForm({ busy = false, error = null, onSubmit, onCancel }: TenantFormProps) {
  const [extAppApiId, setExtAppApiId] = useState('');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [touched, setTouched] = useState(false);

  const effectiveSlug = (slugEdited ? slug : slugFromName(name)).trim();
  const idError = touched && !extAppApiId.trim() ? 'Give the app-api organization id.' : undefined;
  const nameError = touched && !name.trim() ? 'Give the tenant a name.' : undefined;
  const slugError =
    touched && effectiveSlug !== '' && !isValidSlug(effectiveSlug) ? SLUG_RULE : undefined;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (
      !extAppApiId.trim() ||
      !name.trim() ||
      (effectiveSlug !== '' && !isValidSlug(effectiveSlug))
    ) {
      return;
    }
    onSubmit({ extAppApiId: extAppApiId.trim(), name: name.trim(), slug: effectiveSlug || null });
  };

  return (
    <Dialog
      actions={
        <>
          <Button disabled={busy} onClick={onCancel} variant="ghost">
            Cancel
          </Button>
          <Button form="tenant-form" loading={busy} type="submit">
            Add tenant
          </Button>
        </>
      }
      description="If a tenant already exists for this app-api id, it is kept and given this name and slug."
      dismissable={!busy}
      onClose={onCancel}
      title="Add a tenant"
    >
      <form id="tenant-form" className={styles.form} noValidate onSubmit={submit}>
        <TextField
          autoComplete="off"
          data-autofocus
          error={idError}
          hint="The organization's id in app-api. It can't be changed later."
          label="app-api organization id"
          onChange={(event) => setExtAppApiId(event.target.value)}
          value={extAppApiId}
        />
        <TextField
          autoComplete="off"
          error={nameError}
          label="Tenant name"
          onChange={(event) => setName(event.target.value)}
          placeholder="e.g. Finance Solutions"
          value={name}
        />
        <TextField
          autoComplete="off"
          error={slugError}
          hint="Filled in from the name. Leave empty for none."
          label="Slug"
          onChange={(event) => {
            setSlugEdited(true);
            setSlug(event.target.value);
          }}
          value={slugEdited ? slug : effectiveSlug}
        />
        {error ? (
          <Callout tone="danger" title="That didn't work">
            {error}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  );
}
